import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { SearchService, SearchResponse } from '../services/search.service';
import { SearchComponent } from './search.component';

interface ResultGroup {
  label: string;
  icon: string;
  route: string;
  items: { id: number; title: string; subtitle?: string; status?: string }[];
}

@Component({
  standalone: true,
  selector: 'app-search-results',
  templateUrl: './search-results.component.html',
  styleUrl: './search-results.component.css',
  imports: [
    CommonModule,
    FormsModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatDividerModule,
    SearchComponent
  ]
})
export class SearchResultsComponent implements OnInit {
  query: string = '';
  loading: boolean = false;
  error: string | null = null;
  resultGroups: ResultGroup[] = [];
  totalCount: number = 0;
  private searchRequestId: number = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private searchService: SearchService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      const q = params.get('q') || '';
      this.query = q;
      this.searchService.setQuery(q);
      if (q.trim()) {
        this.runSearch(q.trim());
      } else {
        this.resultGroups = [];
        this.totalCount = 0;
      }
    });
  }

  private runSearch(query: string): void {
    const requestId = ++this.searchRequestId;
    this.loading = true;
    this.error = null;
    this.resultGroups = [];
    this.totalCount = 0;
    this.cdr.detectChanges();

    this.searchService.search(query).subscribe({
      next: (data: SearchResponse) => {
        if (requestId !== this.searchRequestId) {
          return;
        }

        this.loading = false;
        this.cdr.detectChanges();

        requestAnimationFrame(() => {
          if (requestId !== this.searchRequestId) {
            return;
          }
          this.buildGroups(data);
          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        if (requestId !== this.searchRequestId) {
          return;
        }
        console.error('Search error:', err);
        this.error = 'Search failed. Please try again.';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private buildGroups(data: SearchResponse): void {
    const groups: ResultGroup[] = [];

    if (data.products?.length) {
      groups.push({
        label: 'Products',
        icon: 'inventory_2',
        route: '/products',
        items: data.products.map(p => ({
          id: p.id || p.Id,
          title: p.name || p.Name || `Product ${p.id || p.Id}`,
          subtitle: p.catalogNum || p.CatalogNum || ''
        }))
      });
    }

    if (data.customers?.length) {
      groups.push({
        label: 'Customers',
        icon: 'people',
        route: '/customers',
        items: data.customers.map(c => ({
          id: c.id || c.Id,
          title: c.name || c.Name || `Customer ${c.id || c.Id}`,
          subtitle: c.email || c.Email || ''
        }))
      });
    }

    if (data.vendors?.length) {
      groups.push({
        label: 'Vendors',
        icon: 'storefront',
        route: '/vendors',
        items: data.vendors.map(v => ({
          id: v.id || v.Id,
          title: v.name || v.Name || `Vendor ${v.id || v.Id}`,
          subtitle: v.email || v.Email || ''
        }))
      });
    }

    if (data.salesOrders?.length) {
      groups.push({
        label: 'Sales Orders',
        icon: 'receipt_long',
        route: '/orders',
        items: data.salesOrders.map(so => ({
          id: so.id || so.Id,
          title: so.number || so.Number || `Order ${so.id || so.Id}`,
          subtitle: so.customerName || so.CustomerName || '',
          status: so.status || so.Status || ''
        }))
      });
    }

    if (data.purchaseOrders?.length) {
      groups.push({
        label: 'Purchase Orders',
        icon: 'shopping_cart',
        route: '/purchase-orders',
        items: data.purchaseOrders.map(po => ({
          id: po.id || po.Id,
          title: po.number || po.Number || `PO ${po.id || po.Id}`,
          subtitle: po.vendorName || po.VendorName || '',
          status: po.status || po.Status || ''
        }))
      });
    }

    if (data.salesInvoices?.length) {
      groups.push({
        label: 'Sales Invoices',
        icon: 'description',
        route: '/sales-invoices',
        items: data.salesInvoices.map(inv => ({
          id: inv.id || inv.Id,
          title: inv.invoiceNumber || inv.InvoiceNumber || `Invoice ${inv.id || inv.Id}`,
          subtitle: inv.customerName || inv.CustomerName || '',
          status: inv.status || inv.Status || ''
        }))
      });
    }

    if (data.proformas?.length) {
      groups.push({
        label: 'Proformas',
        icon: 'article',
        route: '/proformas',
        items: data.proformas.map(pf => ({
          id: pf.id || pf.Id,
          title: pf.proformaNumber || pf.ProformaNumber || `Proforma ${pf.id || pf.Id}`,
          subtitle: pf.customerName || pf.CustomerName || '',
          status: pf.status || pf.Status || ''
        }))
      });
    }

    this.resultGroups = groups;
    this.totalCount = groups.reduce((sum, g) => sum + g.items.length, 0);
  }

  navigateTo(route: string, id: number): void {
    this.router.navigate([route, id]);
  }

  viewAll(route: string): void {
    this.router.navigate([route]);
  }
}
